import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import firestoreService from '../services/firestoreService';
import AdminHeader from '../components/admin/AdminHeader';
import AdminNavigation from '../components/admin/AdminNavigation';
import './PartnerManagement.css';

const PartnerManagement = () => {
    const [partners, setPartners] = useState([]);
    const [filteredPartners, setFilteredPartners] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingPartner, setEditingPartner] = useState(null);
    const [partnerStats, setPartnerStats] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        websiteUrl: '',
        categoryId: '',
        isActive: true,
        logoFile: null
    });
    const navigate = useNavigate();

    useEffect(() => {
        // Check authentication
        const isAuthenticated = localStorage.getItem('adminAuth');
        if (!isAuthenticated) {
            navigate('/admin');
            return;
        }

        loadPartners();
        loadCategories();
        loadPartnerStats();
    }, [navigate]);

    // Filter partners based on search term, category, and status
    useEffect(() => {
        const filtered = partners.filter(partner => {
            // Search filter - check name, description, or website URL
            const matchesSearch = !searchTerm ||
                (partner.name && partner.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (partner.description && partner.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (partner.websiteUrl && partner.websiteUrl.toLowerCase().includes(searchTerm.toLowerCase()));

            // Category filter
            const matchesCategory = categoryFilter === 'all' ||
                (categoryFilter === 'uncategorized' && !partner.categoryId) ||
                partner.categoryId === categoryFilter;

            // Status filter
            const matchesStatus = statusFilter === 'all' ||
                (statusFilter === 'active' && partner.isActive) ||
                (statusFilter === 'inactive' && !partner.isActive);

            return matchesSearch && matchesCategory && matchesStatus;
        });
        setFilteredPartners(filtered);
    }, [partners, searchTerm, categoryFilter, statusFilter]);

    const loadPartners = async () => {
        try {
            setLoading(true);
            const partnersData = await firestoreService.getPartners();
            setPartners(partnersData);
        } catch (error) {
            console.error('Error loading partners:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategories = async () => {
        try {
            const categoriesData = await firestoreService.getPartnerCategoriesWithColors();
            setCategories(categoriesData);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadPartnerStats = async () => {
        try {
            // Use the new analytics collection for fast statistics
            const stats = {};

            // Get all partners
            const partnerDocs = await firestoreService.getPartners();

            // For each partner, get their click statistics from analytics collection
            for (const partner of partnerDocs) {
                const partnerId = partner.id;
                const partnerName = partner.name;

                try {
                    // Get analytics from partner_clicks collection
                    const statsData = await firestoreService.getPartnerClickStats(partnerId, 30);

                    if (statsData && statsData.dailyStats) {
                        stats[partnerName] = statsData.dailyStats;
                    } else {
                        stats[partnerName] = {};
                    }
                } catch (error) {
                    console.warn(`Error loading stats for partner ${partnerId}:`, error);
                    stats[partnerName] = {};
                }
            }

            setPartnerStats(stats);
        } catch (error) {
            console.error('Error loading partner stats:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingPartner) {
                await firestoreService.updatePartner(editingPartner.id, formData);
            } else {
                await firestoreService.createPartner(formData);
            }
            setShowModal(false);
            setEditingPartner(null);
            setFormData({
                name: '',
                description: '',
                websiteUrl: '',
                categoryId: '',
                isActive: true,
                logoFile: null
            });
            loadPartners();
        } catch (error) {
            console.error('Error saving partner:', error);
            alert('Error saving partner: ' + error.message);
        }
    };

    const handleEdit = (partner) => {
        setEditingPartner(partner);
        setFormData({
            name: partner.name || '',
            description: partner.description || '',
            websiteUrl: partner.websiteUrl || '',
            categoryId: partner.categoryId || '',
            isActive: partner.isActive !== undefined ? partner.isActive : true,
            logoFile: null
        });
        setShowModal(true);
    };

    const handleDelete = async (partnerId) => {
        if (window.confirm('Are you sure you want to delete this partner?')) {
            try {
                await firestoreService.deletePartner(partnerId);
                loadPartners();
            } catch (error) {
                console.error('Error deleting partner:', error);
                alert('Error deleting partner: ' + error.message);
            }
        }
    };

    const togglePartnerStatus = async (partnerId, currentStatus) => {
        try {
            await firestoreService.updatePartner(partnerId, { isActive: !currentStatus });
            loadPartners();
        } catch (error) {
            console.error('Error updating partner status:', error);
            alert('Error updating partner status: ' + error.message);
        }
    };

    const getTotalClicks = (partnerName) => {
        const partnerStat = partnerStats[partnerName] || {};
        return Object.values(partnerStat).reduce((sum, count) => sum + count, 0);
    };

    const getRecentClicks = (partnerName, days = 7) => {
        const partnerStat = partnerStats[partnerName] || {};
        const recentDate = new Date();
        recentDate.setDate(recentDate.getDate() - days);

        let recentClicks = 0;
        Object.entries(partnerStat).forEach(([date, count]) => {
            if (new Date(date) >= recentDate) {
                recentClicks += count;
            }
        });
        return recentClicks;
    };

    const getCategoryName = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.name : 'Unknown';
    };

    const getCategoryColor = (categoryId) => {
        const category = categories.find(cat => cat.id === categoryId);
        return category ? category.color : 'grey';
    };

    return (
        <div className="partner-management">
            <AdminHeader title="Partner Management" />
            <AdminNavigation />

            <main className="admin-main">
                <div className="partner-management-header">
                    <div className="search-filters-section">
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search partners by name, description, or URL..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <select
                            className="filter-select"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                    {category.name}
                                </option>
                            ))}
                            <option value="uncategorized">Uncategorized</option>
                        </select>
                        <select
                            className="filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>
                    <div className="header-actions">
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                setEditingPartner(null);
                                setFormData({
                                    name: '',
                                    description: '',
                                    websiteUrl: '',
                                    categoryId: '',
                                    isActive: true,
                                    logoFile: null
                                });
                                setShowModal(true);
                            }}
                        >
                            + Add New Partner
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="loading">Loading partners...</div>
                ) : (
                    <div className="partners-grid">
                        {filteredPartners.length === 0 ? (
                            <div className="no-results">
                                <p>No partners found matching your search criteria.</p>
                            </div>
                        ) : (
                            filteredPartners.map(partner => (
                                <div key={partner.id} className="partner-card">
                                    <div className="partner-header">
                                        <div className="partner-logo">
                                            {partner.logoUrl ? (
                                                <img src={partner.logoUrl} alt={partner.name || 'Partner'} />
                                            ) : (
                                                <div className="logo-placeholder">
                                                    <span>{(partner.name && partner.name.charAt) ? partner.name.charAt(0) : '?'}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="partner-info">
                                            <h3>{partner.name || 'Unknown Partner'}</h3>
                                            <div
                                                className="category-badge"
                                                style={{ backgroundColor: getCategoryColor(partner.categoryId) + '20', color: getCategoryColor(partner.categoryId) }}
                                            >
                                                {getCategoryName(partner.categoryId)}
                                            </div>
                                        </div>
                                        <div className="partner-status">
                                            <span className={`status ${partner.isActive ? 'active' : 'inactive'}`}>
                                                {partner.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="partner-description">
                                        <p>{partner.description || 'No description available'}</p>
                                    </div>

                                    <div className="partner-stats">
                                        <div className="stat-item">
                                            <span className="stat-label">Total Clicks:</span>
                                            <span className="stat-value">{getTotalClicks(partner.name)}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Last 7 Days:</span>
                                            <span className="stat-value">{getRecentClicks(partner.name, 7)}</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">Last 30 Days:</span>
                                            <span className="stat-value">{getRecentClicks(partner.name, 30)}</span>
                                        </div>
                                    </div>

                                    <div className="partner-actions">
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => window.open(partner.websiteUrl || '#', '_blank')}
                                        >
                                            Visit Website
                                        </button>
                                        <button
                                            className="btn btn-warning"
                                            onClick={() => handleEdit(partner)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className={`btn ${partner.isActive ? 'btn-danger' : 'btn-success'}`}
                                            onClick={() => togglePartnerStatus(partner.id, partner.isActive)}
                                        >
                                            {partner.isActive ? 'Deactivate' : 'Activate'}
                                        </button>
                                        <button
                                            className="btn btn-danger"
                                            onClick={() => handleDelete(partner.id)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* Modal for Add/Edit Partner */}
                {showModal && (
                    <div className="modal-overlay">
                        <div className="modal">
                            <div className="modal-header">
                                <h2>{editingPartner ? 'Edit Partner' : 'Add New Partner'}</h2>
                                <button
                                    className="close-btn"
                                    onClick={() => setShowModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <form onSubmit={handleSubmit} className="modal-form">
                                <div className="form-group">
                                    <label>Partner Name</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Website URL</label>
                                    <input
                                        type="url"
                                        name="websiteUrl"
                                        value={formData.websiteUrl}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Category</label>
                                    <select
                                        name="categoryId"
                                        value={formData.categoryId}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Category</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Logo</label>
                                    <input
                                        type="file"
                                        name="logoFile"
                                        onChange={handleInputChange}
                                        accept="image/png,image/jpg,image/jpeg"
                                    />
                                </div>
                                <div className="form-group checkbox-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="isActive"
                                            checked={formData.isActive}
                                            onChange={handleInputChange}
                                        />
                                        Active
                                    </label>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" onClick={() => setShowModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary">
                                        {editingPartner ? 'Update' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default PartnerManagement;
